import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const CHORD_TIMEOUT = 500; // ms to wait for second key after prefix

function isInputFocused(): boolean {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if ((el as HTMLElement).contentEditable === 'true') return true;
  return false;
}

interface ShortcutCallbacks {
  onSearch?: () => void;
  onHelp?: () => void;
}

export function useKeyboardShortcuts(callbacks: ShortcutCallbacks = {}) {
  const navigate = useNavigate();
  const prefixRef = useRef<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearPrefix = useCallback(() => {
    prefixRef.current = null;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isInputFocused()) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const key = e.key.toLowerCase();

      // ? -> show help
      if (key === '?' || (e.shiftKey && key === '/')) {
        e.preventDefault();
        callbacks.onHelp?.();
        return;
      }

      // Handle chord: G + <key>
      if (prefixRef.current === 'g') {
        clearPrefix();
        e.preventDefault();
        const routes: Record<string, string> = {
          d: '/dashboard',
          l: '/dashboard/leads',
          b: '/dashboard/bookings',
          w: '/dashboard/websites',
          r: '/dashboard/reviews',
          a: '/dashboard/analytics',
          s: '/dashboard/seo',
          p: '/dashboard/content-plan',
          n: '/dashboard/notifications',
        };
        if (routes[key]) {
          navigate(routes[key]);
        }
        return;
      }

      // Start chord with G
      if (key === 'g') {
        prefixRef.current = 'g';
        timerRef.current = setTimeout(clearPrefix, CHORD_TIMEOUT);
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      clearPrefix();
    };
  }, [navigate, callbacks, clearPrefix]);
}
